import {
  Button,
  Collapse,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useDisclosure,
  useTimeout,
  VStack,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { MemberSelectorMember } from 'apps/TenantApp/components/MemberSelector';
import { ErrorMessage } from 'components/ErrorMessage';
import { Textarea } from 'components/Form/Textarea';
import { SuspendWithSpinner } from 'components/SuspendWithSpinner';
import { OverlayProps } from 'components/TaskOverlay';
import differenceBy from 'lodash/differenceBy';
import uniqBy from 'lodash/uniqBy';
import React, { useEffect, useState } from 'react';
import { Member } from 'types';
import { ErrorBoundary } from 'utils/errors';
import { graphql, useMutation, useSafeRelay } from 'utils/graphClient';
import { notEmpty } from 'utils/NotEmptyFilter';
import { ReminderModal_createReminder_Mutation } from './__generated__/ReminderModal_createReminder_Mutation.graphql';
import { ReminderModal_node_Query } from './__generated__/ReminderModal_node_Query.graphql';
import { ReminderPicker } from './ReminderPicker';
import { SuccessIcon } from 'components/icons';

const nodeQuery = graphql`
  query ReminderModal_node_Query($id: ID!) {
    node(id: $id) {
      __typename
      id
      ... on VaultItem {
        signatureEnvelope {
          id
          status
          signatures {
            edges {
              node {
                id
                status
                member {
                  id
                  name
                  avatar {
                    name
                    url
                  }
                  status {
                    value
                  }
                  ...UserHandle_member
                }
              }
            }
          }
        }
        vault {
          members {
            id
            name
            avatar {
              name
              url
            }
            status {
              value
            }
            ...UserHandle_member
          }
        }
      }
      ... on FolderItem {
        signatureEnvelope {
          id
          status
          signatures {
            edges {
              node {
                id
                status
                member {
                  id
                  name
                  avatar {
                    name
                    url
                  }
                  status {
                    value
                  }
                  ...UserHandle_member
                }
              }
            }
          }
        }
        content {
          ... on Form {
            __typename
            responses {
              edges {
                node {
                  id
                  state
                  responder {
                    id
                    name
                    avatar {
                      name
                      url
                    }
                    status {
                      value
                    }
                    ...UserHandle_member
                  }
                }
              }
            }
          }
        }
        ownershipEvent {
          id
          invitations {
            edges {
              node {
                id
                rsvp
                invitee {
                  id
                  name
                  avatar {
                    name
                    url
                  }
                  status {
                    value
                  }
                  ...UserHandle_member
                }
              }
            }
          }
        }
        space {
          memberships {
            edges {
              node {
                id
                member {
                  id
                  name
                  avatar {
                    name
                    url
                  }
                  status {
                    value
                  }
                  ...UserHandle_member
                }
              }
            }
          }
        }
      }
      ... on Event {
        invitations {
          edges {
            node {
              id
              rsvp
              invitee {
                id
                name
                avatar {
                  name
                  url
                }
                status {
                  value
                }
                ...UserHandle_member
              }
            }
          }
        }
        space {
          memberships {
            edges {
              node {
                id
                member {
                  id
                  name
                  avatar {
                    name
                    url
                  }
                  status {
                    value
                  }
                  ...UserHandle_member
                }
              }
            }
          }
        }
      }
    }
  }
`;

const createReminderMutation = graphql`
  mutation ReminderModal_createReminder_Mutation(
    $input: RemindersCreateMutationInput!
  ) {
    createReminder(input: $input) {
      success
    }
  }
`;

interface ReminderModalProps extends OverlayProps {
  nodeId: string;
}

export const ReminderModal = ({ nodeId, ...props }: ReminderModalProps) => {
  return (
    <Modal {...props}>
      <ModalOverlay />

      <ErrorBoundary fallback={<FallbackReminderModalContent />}>
        <SuspendWithSpinner>
          <ReminderModalContent
            nodeId={nodeId}
            onCloseReminderModal={props.onClose}
          />
        </SuspendWithSpinner>
      </ErrorBoundary>
    </Modal>
  );
};

interface ReminderModalContentProps {
  nodeId: string;
  onCloseReminderModal: () => void;
}

enum QuickSelect {
  _, // Ignoring first value. This is 0 and can be confusing when dealing with conditions as 0 is falsy
  EVERYONE_IN_VAULT,
  EVERYONE_IN_SPACE,
  EVERYONE_WHO_HAS_RESPONDED,
  EVERYONE_WHO_HAS_NOT_RESPONDED,
  EVERYONE_FROM_INVITEES_WHO_HAS_NOT_RESPONDED,
  EVERYONE_WHO_HAS_RSVP,
  EVERYONE_WHO_HAS_NOT_RSVP,
  EVERYONE_WHO_WAS_REQUESTED_TO_SIGN,
  EVERYONE_WHO_HAS_NOT_SIGNED,
}

const ReminderModalContent = ({
  nodeId,
  onCloseReminderModal,
}: ReminderModalContentProps) => {
  const { useLazyLoadQuery } = useSafeRelay();
  const { node } = useLazyLoadQuery<ReminderModal_node_Query>(nodeQuery, {
    id: nodeId,
  });

  const [createReminder, loadingCreateReminder] =
    useMutation<ReminderModal_createReminder_Mutation>(createReminderMutation);

  const { isOpen: isCompletedOpen, onOpen: onOpenCompleted } = useDisclosure();

  const [selectedMembers, setSelectedMembers] = useState<Member[]>([]);

  const [message, setMessage] = useState('');

  const [quickSelect, setQuickSelect] = useState(0);

  const handleSendReminder = () => {
    createReminder({
      variables: {
        input: {
          nodeId,
          memberIds: selectedMembers.map((m) => m.id).filter(notEmpty) || [],
          message,
        },
      },
      onCompleted: onOpenCompleted,
    });
  };

  const isVaultItem = node?.__typename === 'VaultItem';
  const isSpace = !!node?.space;
  const isForm = node?.content?.__typename === 'Form';
  const isFormInEvent = isForm && !!node?.ownershipEvent?.id;
  const isEvent = node?.__typename === 'Event';
  const isSigningOngoing = node?.signatureEnvelope?.status === 'SIGNING';
  const isSigningCompleted = node?.signatureEnvelope?.status === 'COMPLETED';
  const quickSelectOptions = [
    {
      value: QuickSelect.EVERYONE_IN_VAULT,
      label: t`Everyone in this vault`,
      visible: isVaultItem,
    },
    {
      value: QuickSelect.EVERYONE_IN_SPACE,
      label: t`Everyone in this space`,
      visible: isSpace,
    },
    {
      value: QuickSelect.EVERYONE_WHO_HAS_RESPONDED,
      label: t`Everyone who has responded`,
      visible: isForm,
    },
    {
      value: QuickSelect.EVERYONE_WHO_HAS_NOT_RESPONDED,
      label: isFormInEvent
        ? t`Everyone who can see the event and has not responded`
        : t`Everyone who has not responded`,
      visible: isForm,
    },
    {
      value: QuickSelect.EVERYONE_FROM_INVITEES_WHO_HAS_NOT_RESPONDED,
      label: t`Everyone from the guest list who has not responded`,
      visible: isFormInEvent,
    },
    {
      value: QuickSelect.EVERYONE_WHO_HAS_RSVP,
      label: t`Everyone who has RSVP`,
      visible: isEvent || isFormInEvent,
    },
    {
      value: QuickSelect.EVERYONE_WHO_HAS_NOT_RSVP,
      label: t`Everyone who has not RSVP`,
      visible: isEvent || isFormInEvent,
    },
    {
      value: QuickSelect.EVERYONE_WHO_WAS_REQUESTED_TO_SIGN,
      label: t`Everyone who is requested to sign`,
      visible: isSigningOngoing || isSigningCompleted,
    },
    {
      value: QuickSelect.EVERYONE_WHO_HAS_NOT_SIGNED,
      label: t`Everyone who has not signed yet`,
      visible: isSigningOngoing && !isSigningCompleted,
    },
  ];

  const spaceMembers = node?.space?.memberships.edges
    ?.map((e) => e.node?.member)
    .filter(notEmpty);
  const vaultMembers = node?.vault ? [...node.vault.members] : [];
  const availableMembers: MemberSelectorMember[] = spaceMembers || vaultMembers;

  useEffect(() => {
    let quickSelectedMembers: Member[] = [];
    switch (quickSelect) {
      case QuickSelect.EVERYONE_IN_VAULT:
      case QuickSelect.EVERYONE_IN_SPACE:
        quickSelectedMembers = availableMembers;
        break;
      case QuickSelect.EVERYONE_WHO_HAS_RESPONDED:
        if (isForm) {
          quickSelectedMembers =
            node?.content?.responses.edges
              ?.map(({ node }) =>
                node?.state === 'SUBMITTED' ? node.responder : null,
              )
              .filter(notEmpty) || [];
        }
        break;
      case QuickSelect.EVERYONE_WHO_HAS_NOT_RESPONDED:
        if (isForm) {
          const formResponders =
            node?.content.responses.edges
              ?.map(({ node }) =>
                node?.state === 'SUBMITTED' ? node.responder : null,
              )
              .filter(notEmpty) || [];

          quickSelectedMembers = differenceBy(
            availableMembers,
            formResponders,
            'id',
          );
        }
        break;
      case QuickSelect.EVERYONE_FROM_INVITEES_WHO_HAS_NOT_RESPONDED:
        if (isFormInEvent) {
          const eventInvitees =
            node?.ownershipEvent?.invitations?.edges
              ?.map((edge) => edge.node?.invitee)
              .filter(notEmpty) || [];

          const formResponders =
            node?.content.responses.edges
              ?.map(({ node }) =>
                node?.state === 'SUBMITTED' ? node.responder : null,
              )
              .filter(notEmpty) || [];

          quickSelectedMembers = differenceBy(
            eventInvitees,
            formResponders,
            'id',
          );
        }
        break;
      case QuickSelect.EVERYONE_WHO_HAS_RSVP:
        if (isEvent) {
          quickSelectedMembers =
            node.invitations?.edges
              ?.map((edge) =>
                edge.node?.rsvp !== 'NOT_RESPONDED' ? edge.node?.invitee : null,
              )
              .filter(notEmpty) || [];
        }
        if (isFormInEvent) {
          quickSelectedMembers =
            node?.ownershipEvent?.invitations?.edges
              ?.map((edge) =>
                edge.node?.rsvp !== 'NOT_RESPONDED' ? edge.node?.invitee : null,
              )
              .filter(notEmpty) || [];
        }
        break;
      case QuickSelect.EVERYONE_WHO_HAS_NOT_RSVP:
        if (isEvent) {
          quickSelectedMembers =
            node.invitations?.edges
              ?.map((edge) =>
                edge.node?.rsvp === 'NOT_RESPONDED' ? edge.node?.invitee : null,
              )
              .filter(notEmpty) || [];
        }
        if (isFormInEvent) {
          quickSelectedMembers =
            node?.ownershipEvent?.invitations?.edges
              ?.map((edge) =>
                edge.node?.rsvp === 'NOT_RESPONDED' ? edge.node?.invitee : null,
              )
              .filter(notEmpty) || [];
        }
        break;
      case QuickSelect.EVERYONE_WHO_WAS_REQUESTED_TO_SIGN:
        quickSelectedMembers =
          node?.signatureEnvelope?.signatures?.edges
            ?.map((edge) => edge.node?.member)
            .filter(notEmpty) || [];
        break;
      case QuickSelect.EVERYONE_WHO_HAS_NOT_SIGNED:
        quickSelectedMembers =
          node?.signatureEnvelope?.signatures?.edges
            ?.map((edge) =>
              edge.node?.status === 'WAITING' ? edge.node?.member : null,
            )
            .filter(notEmpty) || [];
        break;
    }

    setSelectedMembers(uniqBy(quickSelectedMembers, 'id'));
  }, [quickSelect]);

  if (isCompletedOpen) {
    return <Completed onClose={onCloseReminderModal} />;
  }

  return (
    <ModalContent pb={2} data-qa="create-reminder-dialog">
      <ModalHeader>
        <Text>{t`Send a reminder`}</Text>
      </ModalHeader>

      <ModalCloseButton />

      <ModalBody>
        <Stack spacing={4}>
          <ReminderPicker
            options={quickSelectOptions}
            availableMembers={availableMembers}
            selectedMembers={selectedMembers}
            quickSelect={quickSelect}
            confirmQuickSelectChange
            onQuickSelectChange={setQuickSelect}
            onManualSelectChange={setSelectedMembers}
          />

          <Textarea
            label={t`Message`}
            placeholder={t`Your feedback`}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </Stack>
      </ModalBody>

      <ModalFooter>
        <Button
          colorScheme="blue"
          onClick={handleSendReminder}
          isLoading={loadingCreateReminder}
          isDisabled={!selectedMembers.length}
        >
          {t`Send`}
        </Button>
      </ModalFooter>
    </ModalContent>
  );
};

const FallbackReminderModalContent = () => {
  return (
    <ModalContent pb={3}>
      <ModalHeader>
        <Text>{t`Send a reminder`}</Text>
      </ModalHeader>

      <ModalCloseButton />

      <ModalBody>
        <ErrorMessage description={t`Unable to load reminder data`} />
      </ModalBody>
    </ModalContent>
  );
};

const Completed = ({ onClose }: { onClose: () => void }) => {
  const [showMessage, setShowMessage] = useState(false);
  useTimeout(() => setShowMessage(true), 10);
  return (
    <ModalContent pb={2}>
      <ModalHeader>
        <Text>{t`Send a reminder`}</Text>
      </ModalHeader>

      <ModalCloseButton />

      <ModalBody>
        <Collapse in={showMessage} animateOpacity>
          <VStack spacing={4}>
            <SuccessIcon size="4x" color="green.500" />
            <Text>{t`Your reminder is sent`}</Text>
          </VStack>
        </Collapse>
      </ModalBody>
      <ModalFooter>
        <Button onClick={onClose}>{t`Close`}</Button>
      </ModalFooter>
    </ModalContent>
  );
};
